import { useState } from 'react';
import { useAssistantNoteApi } from '../Services/AssistantNoteApiProvider';
import { AssistantNoteApi } from '../Services/AssistantNoteApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import AgentNoteDetail, { getNoteSourceLabel } from './AgentNoteDetail';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import { useConfirmation } from './ConfirmationContext';
import Table from './Tables';

interface AgentNotesListProps {
  canEdit: boolean;
  notes: AssistantNoteApi[];
  onUpdateTrigger: () => void;
  onOpenRun: (note: AssistantNoteApi) => void;
  onExport: (noteIds: string[]) => void;
  loading: boolean;
  totalNotes: number;
  persistedPageSize: number;
  tableTitle: string;
  onChangePage: (skip: number, limit: number) => void;
}

const TEXT_PREVIEW_LIMIT = 90;

function AgentNotesList({
  canEdit,
  notes = [],
  onUpdateTrigger,
  onOpenRun,
  onExport,
  loading,
  totalNotes,
  persistedPageSize,
  tableTitle,
  onChangePage,
}: AgentNotesListProps) {
  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();
  const { deleteAssistantNote } = useAssistantNoteApi();

  const [selectedRows, setSelectedRows] = useState<AssistantNoteApi[]>([]);
  const [openedNote, setOpenedNote] = useState<AssistantNoteApi | null>(null);

  const columns = [
    {
      name: 'Type',
      selector: (row: AssistantNoteApi) => row.note_type ?? '',
      sortable: false,
    },
    {
      name: 'Note',
      selector: (row: AssistantNoteApi) => {
        const text = row.text ?? '';
        return text.length > TEXT_PREVIEW_LIMIT
          ? `${text.substring(0, TEXT_PREVIEW_LIMIT)}...`
          : text;
      },
      sortable: false,
    },
    {
      name: 'Source',
      selector: (row: AssistantNoteApi) => getNoteSourceLabel(row.source),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: AssistantNoteApi) => formatDateTimeLocal(row.created_at),
      sortable: false,
    },
  ];

  const handleDelete = (selectedIds: string[]): void => {
    if (selectedIds.length === 0 || !canEdit) return;

    const question =
      selectedIds.length === 1
        ? 'Are you sure you want to delete this note?'
        : `Are you sure you want to delete ${selectedIds.length} notes?`;

    showConfirmation(question, async () => {
      try {
        for (const noteId of selectedIds) {
          await deleteAssistantNote(noteId);
        }
        setOpenedNote(null);
        onUpdateTrigger();
      } catch (error) {
        showAlert('fail', 'Failed to delete the notes.');
        onUpdateTrigger();
      }
    });
  };

  // The table remounts on the way back and starts with nothing selected
  const handleOpenNote = (note: AssistantNoteApi): void => {
    setSelectedRows([]);
    setOpenedNote(note);
  };

  if (openedNote) {
    return (
      <div className="radius-yes p-5 bg-yes col-12">
        <AgentNoteDetail
          note={openedNote}
          canEdit={canEdit}
          onClose={() => setOpenedNote(null)}
          onOpenRun={onOpenRun}
          onExport={onExport}
          onDelete={handleDelete}
        />
      </div>
    );
  }

  return (
    <div className="radius-yes p-5 bg-yes col-12">
      <Table
        title={tableTitle}
        columns={columns}
        data={notes}
        onRowClicked={handleOpenNote}
        onDelete={handleDelete}
        onSelectedRowsChange={({ selectedRows: rows }: { selectedRows: AssistantNoteApi[] }) =>
          setSelectedRows(rows)
        }
        contextActionsExtra={
          <Button
            variant="ghost"
            size="medium"
            disabled={selectedRows.length === 0}
            onClick={() => onExport(selectedRows.map((row) => row.id))}
          >
            Export Selected
          </Button>
        }
        selectableRows={canEdit}
        singleSelect={false}
        selectableRowsHighlight
        pagination
        progressPending={loading}
        paginationTotalRows={totalNotes}
        paginationPerPage={persistedPageSize}
        onChangePage={onChangePage}
      />
    </div>
  );
}

export default AgentNotesList;
